import type { parseFilterModel } from '@/lib/createAgGridDatasource';
import { buildDownloadOptions as buildDownloadOptionsShared, type SortColumnState } from '@/lib/downloadOptions';
import type { OrgSamplesDownloadOptions } from '@/services/samples';
import type { OrgSampleFilters, SampleSortField } from '@/types';

// The SampleSortField values that are also real, field-backed columns on this grid — a few
// sortable columns here (sample_group, flow_cell, etc.) are colId-only placeholders with no
// backend counterpart, same as instrument_runs' own exceptions.
export const VALID_SORT_FIELDS: readonly SampleSortField[] = [
  'name',
  'well_position',
  'demux_index',
  'study_name',
  'instrument_run_name',
  'assay_friendly_name',
  'sample_quality_qc',
  'decode_qc',
  'lane_qc',
  'controls_qc',
  'sample_qc_outcome',
  'warning',
  'created_at',
  'updated_at',
];

// OrgSampleFilters' actual keys (src/types/api.ts) — the grid also filters on well_position,
// demux_index, sample_group, created_at, and updated_at, none of which this endpoint's filter
// contract supports.
const VALID_FILTER_FIELDS: readonly (keyof OrgSampleFilters)[] = [
  'name',
  'instrument_run_name',
  'assay_friendly_name',
  'sample_type',
  'species',
  'sample_quality_qc',
  'decode_qc',
  'lane_qc',
  'controls_qc',
  'sample_qc_outcome',
  'warning',
  'study_name',
];

/**
 * Pure translation of the grid's current sort/filter state into the CSV export's options.
 * Kept out of the page component so the sort-field gate and the filters-length branch can be
 * unit tested without mounting the grid.
 */
export function buildDownloadOptions(
  columnState: readonly SortColumnState[],
  filterModel: Parameters<typeof parseFilterModel>[0],
): OrgSamplesDownloadOptions {
  // `warning` is the one boolean-filtered column on this grid; without this the export sends
  // AG Grid's stringified "true"/"false" instead of a real boolean, same as the grid's own datasource.
  return buildDownloadOptionsShared(
    columnState,
    filterModel,
    VALID_SORT_FIELDS,
    VALID_FILTER_FIELDS,
    new Set(['warning']),
  );
}
